/**
 * "Which nav item is the current page?" (W-M10).
 *
 * react-router's NavLink matches on pathname only, which is wrong for the Admin > People & Access
 * items: Employees, Classifications & Rates and Roles & Permissions are all `/users?tab=...`, so
 * NavLink marked all three as current at once. These helpers match the pathname (exactly, or as a
 * prefix on a `/` boundary so `/vendors/12` belongs to Vendors but `/vendor-approvals` does not)
 * and then prefer the item whose query parameters also match.
 */

export interface NavLocation {
  pathname: string;
  search: string;
}

const splitTo = (to: string): { pathname: string; params: URLSearchParams } => {
  const [pathname, query = ''] = to.split('?');
  return { pathname: pathname || '/', params: new URLSearchParams(query) };
};

const trimSlash = (path: string): string => (path.length > 1 ? path.replace(/\/+$/, '') : path);

/** True when `pathname` is `itemPath` or a page beneath it (`/vendors` owns `/vendors/12`). */
export function pathMatches(pathname: string, itemPath: string): boolean {
  const current = trimSlash(pathname);
  const item = trimSlash(itemPath);
  if (item === '/') return current === '/';
  return current === item || current.startsWith(`${item}/`);
}

/**
 * Score how well `to` describes `location`; 0 means it is not this page at all.
 * Longer paths beat shorter ones, and an item whose query params all match beats one that only
 * shares the pathname.
 */
export function scoreNavMatch(location: NavLocation, to: string): number {
  const { pathname, params } = splitTo(to);
  if (!pathMatches(location.pathname, pathname)) return 0;
  const current = new URLSearchParams(location.search);
  let queryMatches = true;
  params.forEach((value, key) => {
    if (current.get(key) !== value) queryMatches = false;
  });
  return trimSlash(pathname).length * 2 + (queryMatches ? 1 : 0) + 1;
}

/**
 * The single destination in `candidates` that is the current page, or null.
 *
 * When several share the pathname and none matches the query (e.g. plain `/users`, where the page
 * picks its own default tab), the first candidate wins - menus list the default tab first.
 */
export function pickCurrentPath(candidates: string[], location: NavLocation): string | null {
  let best: string | null = null;
  let bestScore = 0;
  for (const to of candidates) {
    const score = scoreNavMatch(location, to);
    if (score > bestScore) {
      best = to;
      bestScore = score;
    }
  }
  return best;
}
